import type {AnalyticsPayload, EcommerceItem, OurPropertySectionType, SearchResultsEventPayload, SimpleSearchEventPayload} from "./analytics.types";

export const ANALYTICS_MIGRATION_PHASE = "gtm_segment_dual_emit";
export const ANALYTICS_SCHEMA_VERSION = "1.0";

export const CanonicalAnalyticsEventName = {
    SearchSubmitted: "Search Submitted",
    SearchResultsViewed: "Search Results Viewed",
    SearchResultsEmpty: "Search Results Empty",
    RentalViewed: "Rental Viewed",
    PropertyResultsShuffled: "Property Results Shuffled",
    PropertyTagClicked: "Property Tag Clicked",
    PropertyResultsLoadMoreClicked: "Property Results Load More Clicked",
    QuoteRequested: "Quote Requested",
    CheckoutStarted: "Checkout Started",
    CheckoutStepViewed: "Checkout Step Viewed",
    CheckoutValidationFailed: "Checkout Validation Failed",
    BookingFailed: "Booking Failed",
    BookingCompleted: "Booking Completed",
    InquiryFormOpened: "Inquiry Form Opened",
    InquirySubmitted: "Inquiry Submitted",
    InquiryValidationFailed: "Inquiry Validation Failed",
} as const;

export const LegacyGtmEventName = {
    SimpleSearch: "simple_search",
    ViewSearchResults: "view_search_results",
    NoResults: "no_results",
    ViewItem: "view_item",
    PropertyShuffle: "property_shuffle",
    PropertyTagClick: "property_tag_click",
    PropertyLoadMore: "property_load_more",
    QuoteRequested: "quote_requested",
    BeginCheckout: "begin_checkout",
    CheckoutStep1: "checkout_step_1",
    CheckoutStep2: "checkout_step_2",
    CheckoutStep3: "checkout_step_3",
    CheckoutValidationError: "checkout_validation_error",
    CheckoutBookingError: "checkout_booking_error",
    Purchase: "purchase",
    InquiryFormOpen: "inquiry_form_open",
    InquiryFormSubmit: "inquiry_form_submit",
    InquiryFormValidationError: "inquiry_form_validation_error",
} as const;

type ValueOf<T> = T[keyof T];

export type AnalyticsSource = "client" | "server" | "dual_client" | "dual_server";
export type CanonicalAnalyticsEventNameValue = ValueOf<typeof CanonicalAnalyticsEventName>;
export type LegacyGtmEventNameValue = ValueOf<typeof LegacyGtmEventName>;

export interface AnalyticsMetadata {
    analytics_source: AnalyticsSource;
    schema_version: typeof ANALYTICS_SCHEMA_VERSION;
    event_id?: string;
}

export interface GtmMigrationMetadata {
    legacy_gtm_event: LegacyGtmEventNameValue;
    migration_phase: typeof ANALYTICS_MIGRATION_PHASE;
}

export type SegmentTrackProperties = AnalyticsPayload & AnalyticsMetadata & Partial<GtmMigrationMetadata>;

export interface SegmentTrackMapping {
    eventName: CanonicalAnalyticsEventNameValue;
    properties: SegmentTrackProperties;
}

export interface CanonicalEventBase<TName extends CanonicalAnalyticsEventNameValue, TProperties extends object> {
    name: TName;
    properties: TProperties;
    eventId?: string;
    source?: AnalyticsSource;
}

export type SearchSubmittedEvent = CanonicalEventBase<typeof CanonicalAnalyticsEventName.SearchSubmitted, SimpleSearchEventPayload>;
export type SearchResultsViewedEvent = CanonicalEventBase<typeof CanonicalAnalyticsEventName.SearchResultsViewed, SearchResultsEventPayload>;
export type SearchResultsEmptyEvent = CanonicalEventBase<typeof CanonicalAnalyticsEventName.SearchResultsEmpty, SearchResultsEventPayload>;

export type RentalViewedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.RentalViewed,
    {
        unit_id: string;
        unit_name: string;
        value: number;
        currency?: string;
    }
>;

export type PropertyResultsShuffledEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.PropertyResultsShuffled,
    {section_type: OurPropertySectionType}
>;

export type PropertyTagClickedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.PropertyTagClicked,
    {tag: string; section_type: OurPropertySectionType}
>;

export type PropertyResultsLoadMoreClickedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.PropertyResultsLoadMoreClicked,
    {section_type: OurPropertySectionType}
>;

export type QuoteRequestedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.QuoteRequested,
    {
        unit_id: string;
        check_in: string;
        check_out: string;
        adults: number;
        children: number;
        pets: number;
        total_price: number;
        currency: string;
        success: boolean;
    }
>;

export type CheckoutStartedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.CheckoutStarted,
    {
        unit_id: string;
        value: number;
        currency: string;
        checkout_id?: string;
        items?: Array<EcommerceItem>;
    }
>;

export type CheckoutStepViewedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.CheckoutStepViewed,
    {
        checkout_id: string;
        step_index: 1 | 2 | 3;
        step_name: string;
    }
>;

export type CheckoutValidationFailedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.CheckoutValidationFailed,
    {
        checkout_id: string;
        step_index: number;
        step_name: string;
        fields: string;
    }
>;

export type BookingFailedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.BookingFailed,
    {
        checkout_id: string;
        error_code: string;
    }
>;

export type BookingCompletedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.BookingCompleted,
    {
        transaction_id: string;
        value: number;
        currency: string;
        items: Array<EcommerceItem>;
    }
>;

export type InquiryFormOpenedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.InquiryFormOpened,
    {
        unit_id: string;
        check_in: string | null;
        check_out: string | null;
        adults: number;
        children: number;
        pets: number;
        total_price: number;
    }
>;

export type InquirySubmittedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.InquirySubmitted,
    | {
          success: true;
          unit_id: string;
          check_in: string;
          check_out: string;
          adults: number;
          children: number;
          pets: number;
      }
    | {
          success: false;
          unit_id: string;
          check_in: string | null;
          check_out: string | null;
          adults: number;
          children: number;
          pets: number;
          error_code: string;
      }
>;

export type InquiryValidationFailedEvent = CanonicalEventBase<
    typeof CanonicalAnalyticsEventName.InquiryValidationFailed,
    {
        unit_id: string;
        fields: string;
    }
>;

export type CanonicalAnalyticsEvent =
    | SearchSubmittedEvent
    | SearchResultsViewedEvent
    | SearchResultsEmptyEvent
    | RentalViewedEvent
    | PropertyResultsShuffledEvent
    | PropertyTagClickedEvent
    | PropertyResultsLoadMoreClickedEvent
    | QuoteRequestedEvent
    | CheckoutStartedEvent
    | CheckoutStepViewedEvent
    | CheckoutValidationFailedEvent
    | BookingFailedEvent
    | BookingCompletedEvent
    | InquiryFormOpenedEvent
    | InquirySubmittedEvent
    | InquiryValidationFailedEvent;

export function createAnalyticsMetadata({eventId, source}: {eventId?: string; source: AnalyticsSource}): AnalyticsMetadata {
    return {
        analytics_source: source,
        schema_version: ANALYTICS_SCHEMA_VERSION,
        ...(eventId && {event_id: eventId}),
    };
}

export function createGtmMigrationMetadata(legacyGtmEvent: LegacyGtmEventNameValue): GtmMigrationMetadata {
    return {
        legacy_gtm_event: legacyGtmEvent,
        migration_phase: ANALYTICS_MIGRATION_PHASE,
    };
}

function getCheckoutStepGtmEventName(stepIndex: 1 | 2 | 3): LegacyGtmEventNameValue {
    switch (stepIndex) {
        case 1:
            return LegacyGtmEventName.CheckoutStep1;
        case 2:
            return LegacyGtmEventName.CheckoutStep2;
        case 3:
            return LegacyGtmEventName.CheckoutStep3;
    }
}

export function findLegacyGtmEventName(event: CanonicalAnalyticsEvent): LegacyGtmEventNameValue | null {
    switch (event.name) {
        case CanonicalAnalyticsEventName.SearchSubmitted:
            return LegacyGtmEventName.SimpleSearch;
        case CanonicalAnalyticsEventName.SearchResultsViewed:
            return LegacyGtmEventName.ViewSearchResults;
        case CanonicalAnalyticsEventName.SearchResultsEmpty:
            return LegacyGtmEventName.NoResults;
        case CanonicalAnalyticsEventName.RentalViewed:
            return LegacyGtmEventName.ViewItem;
        case CanonicalAnalyticsEventName.PropertyResultsShuffled:
            return LegacyGtmEventName.PropertyShuffle;
        case CanonicalAnalyticsEventName.PropertyTagClicked:
            return LegacyGtmEventName.PropertyTagClick;
        case CanonicalAnalyticsEventName.PropertyResultsLoadMoreClicked:
            return LegacyGtmEventName.PropertyLoadMore;
        case CanonicalAnalyticsEventName.QuoteRequested:
            return LegacyGtmEventName.QuoteRequested;
        case CanonicalAnalyticsEventName.CheckoutStarted:
            return LegacyGtmEventName.BeginCheckout;
        case CanonicalAnalyticsEventName.CheckoutStepViewed:
            return getCheckoutStepGtmEventName(event.properties.step_index);
        case CanonicalAnalyticsEventName.CheckoutValidationFailed:
            return LegacyGtmEventName.CheckoutValidationError;
        case CanonicalAnalyticsEventName.BookingFailed:
            return LegacyGtmEventName.CheckoutBookingError;
        case CanonicalAnalyticsEventName.BookingCompleted:
            return LegacyGtmEventName.Purchase;
        case CanonicalAnalyticsEventName.InquiryFormOpened:
            return LegacyGtmEventName.InquiryFormOpen;
        case CanonicalAnalyticsEventName.InquirySubmitted:
            return LegacyGtmEventName.InquiryFormSubmit;
        case CanonicalAnalyticsEventName.InquiryValidationFailed:
            return LegacyGtmEventName.InquiryFormValidationError;
        default:
            return null;
    }
}

export function mapCanonicalEventToGtmPayloads(event: CanonicalAnalyticsEvent): Array<AnalyticsPayload> {
    const legacyEvent = findLegacyGtmEventName(event);

    if (!legacyEvent) return [];

    switch (event.name) {
        case CanonicalAnalyticsEventName.RentalViewed:
            return [
                {ecommerce: null},
                {
                    event: legacyEvent,
                    ecommerce: {
                        currency: event.properties.currency ?? "USD",
                        value: event.properties.value,
                        items: [
                            {
                                item_id: event.properties.unit_id,
                                item_name: event.properties.unit_name,
                                price: event.properties.value,
                            },
                        ],
                    },
                },
            ];
        case CanonicalAnalyticsEventName.QuoteRequested:
            return [
                {
                    event: legacyEvent,
                    unit_id: event.properties.unit_id,
                    check_in: event.properties.check_in,
                    check_out: event.properties.check_out,
                    adults: event.properties.adults,
                    children: event.properties.children,
                    pets: event.properties.pets,
                    total_price: event.properties.total_price,
                    currency: event.properties.currency,
                    has_error: !event.properties.success,
                },
            ];
        case CanonicalAnalyticsEventName.CheckoutStarted:
            return [
                {ecommerce: null},
                {
                    event: legacyEvent,
                    ecommerce: {
                        currency: event.properties.currency,
                        value: event.properties.value,
                        items: event.properties.items ?? [
                            {
                                item_id: event.properties.unit_id,
                                price: event.properties.value,
                                quantity: 1,
                            },
                        ],
                    },
                },
            ];
        case CanonicalAnalyticsEventName.CheckoutStepViewed:
            return [
                {
                    event: legacyEvent,
                    checkout_id: event.properties.checkout_id,
                    step_name: event.properties.step_name,
                },
            ];
        case CanonicalAnalyticsEventName.BookingCompleted:
            return [{ecommerce: null}, {event: legacyEvent, ecommerce: event.properties}];
        default:
            return [{event: legacyEvent, ...event.properties}];
    }
}

export function mapCanonicalEventToSegmentTrack(event: CanonicalAnalyticsEvent): SegmentTrackMapping {
    const legacyGtmEvent = findLegacyGtmEventName(event);
    const source = event.source ?? (legacyGtmEvent ? "dual_client" : "client");

    return {
        eventName: event.name,
        properties: {
            ...event.properties,
            ...createAnalyticsMetadata({eventId: event.eventId, source}),
            ...(legacyGtmEvent && createGtmMigrationMetadata(legacyGtmEvent)),
        },
    };
}
